import React, { useState, useCallback, useRef, useEffect } from 'react';
import { graphql, useFragment, type EntryPointComponent } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import JQLBuilder from '@atlassian/jira-jql-builder/src/main.tsx';
import { useJqlDebuggerStoreActions } from '@atlassian/jira-jql-debugger/src/controllers/jql-debugger-data/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import type { JQLBuilderWithAIProps } from '../common/types.tsx';
import { useNaturalLanguageMode } from '../controllers/natural-language-mode/index.tsx';
import type { JQLBuilderWithAI$key as FragmentKey } from './__generated__/JQLBuilderWithAI.graphql';
import { AIPremiumUpsellButtonPopup } from './ai-premium-upsell-button-popup/index.tsx';
import { JQLBuilderWithAIFilters } from './filters/JQLBuilderWithAIFilters.tsx';
import { NaturalLanguageToJQLWrapper } from './natural-language-to-jql-wrapper/NaturalLanguageToJQLWrapper.tsx';
import { NaturalLanguageToggle } from './natural-language-toggle/NaturalLanguageToggle.tsx';
import { SpotlightTourContainer } from './spotlight-tour-container/SpotlightTourContainer.tsx';

const JQLBuilderWithAI = ({
	onToggleAi,
	fragmentKey,
	maybeHydrationQueryReference,
	filterRefinementQueryReference,
	...props
}: JQLBuilderWithAIProps): React.JSX.Element => {
	const data = useFragment<FragmentKey>(
		graphql`
			fragment JQLBuilderWithAI on JiraQuery {
				shouldShowAtlassianIntelligence(
					cloudId: $cloudId
					atlassianIntelligenceProductFeatureInput: {
						feature: $atlassianIntelligenceProductFeature
					}
				) @optIn(to: "JiraNaturalLanguageToJQL")
				...JQLBuilderWithAIFilters
				...NaturalLanguageToggle
				...NaturalLanguageToJQLWrapper_withAIFilters
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					...SpotlightTourContainer
					...NaturalLanguageToJQLWrapper
					...main_jqlBuilder_JQLBuilder
				}
			}
		`,
		fragmentKey,
	);
	const isAtlassianIntelligenceEnabled = !!data?.shouldShowAtlassianIntelligence;
	const userPreferencesData = data?.userPreferences || null;

	const [isNaturalLanguageMode, { toggleNaturalLanguageMode, exitNaturalLanguageMode }] =
		useNaturalLanguageMode();

	const jqlDebuggerStoreActions = useJqlDebuggerStoreActions();
	jqlDebuggerStoreActions?.setExitNaturalLanguageMode(exitNaturalLanguageMode);

	const autoFocusNLToggle = useRef(false);

	const [autoFocusState, setAutoFocusState] = useState(false);

	// This function is used to reset the autofocus of the NL2JQL toggle button as part of the NL2JQL CTA Experiment,
	// which uses the new Atlaskit button where the autofocus bug is present.
	const onToggleBlur = useCallback(() => {
		setAutoFocusState(false);
	}, []);

	const onToggleNaturalLanguageMode = useCallback(() => {
		onToggleAi && onToggleAi(!isNaturalLanguageMode);
		toggleNaturalLanguageMode();

		// Return focus to the toggle button when exiting NL mode
		autoFocusNLToggle.current = true;
		setAutoFocusState(true);
	}, [isNaturalLanguageMode, onToggleAi, toggleNaturalLanguageMode]);

	useEffect(() => {
		autoFocusNLToggle.current = false;
	});

	const isPageHydrating = useIsHydrateRenderMode();

	// Delegate to JQLBuilder if NL is not enabled
	if (!isAtlassianIntelligenceEnabled) {
		return fg('list_lovability_improving_filters') ? (
			<JQLBuilderWithAIFilters
				{...props}
				fragmentKey={data ?? null}
				beforeControls={<AIPremiumUpsellButtonPopup />}
				maybeHydrationQueryReference={
					fg('nin_request_hydrate_query_earlier') ? maybeHydrationQueryReference : undefined
				}
				filterRefinementQueryReference={filterRefinementQueryReference}
			/>
		) : (
			<JQLBuilder
				{...props}
				userPreferencesData={userPreferencesData}
				beforeControls={<AIPremiumUpsellButtonPopup />}
				maybeHydrationQueryReference={
					fg('nin_request_hydrate_query_earlier') ? maybeHydrationQueryReference : undefined
				}
			/>
		);
	}

	// The gate "ai-event-for-nl2jql-cta-change" is the targeting gate for the NL2JQL CTA Experiment. We use this as the autofucs
	// bug only appears on the experiment variant when we use the new Atlaskit button.
	// Bug explanation: https://www.loom.com/share/3485291b949c47a28e21e7ebc813d8ba?sid=78a8c293-8485-4861-92e9-077a45d7e8c5
	const JQLBuilderWithAIComponent = fg('list_lovability_improving_filters') ? (
		<JQLBuilderWithAIFilters
			{...props}
			fragmentKey={data ?? null}
			beforeControls={
				<NaturalLanguageToggle
					autoFocus={
						fg('ai-event-for-nl2jql-cta-change') ? autoFocusState : autoFocusNLToggle.current
					}
					onClick={onToggleNaturalLanguageMode}
					fragmentRef={data}
					{...(fg('ai-event-for-nl2jql-cta-change') ? { onToggleBlur } : {})}
				/>
			}
			maybeHydrationQueryReference={
				fg('nin_request_hydrate_query_earlier') ? maybeHydrationQueryReference : undefined
			}
			filterRefinementQueryReference={filterRefinementQueryReference}
		/>
	) : (
		<JQLBuilder
			{...props}
			userPreferencesData={userPreferencesData}
			beforeControls={
				<NaturalLanguageToggle
					autoFocus={
						fg('ai-event-for-nl2jql-cta-change') ? autoFocusState : autoFocusNLToggle.current
					}
					onClick={onToggleNaturalLanguageMode}
					fragmentRef={data}
					{...(fg('ai-event-for-nl2jql-cta-change') ? { onToggleBlur } : {})}
				/>
			}
			maybeHydrationQueryReference={
				fg('nin_request_hydrate_query_earlier') ? maybeHydrationQueryReference : undefined
			}
		/>
	);

	return (
		<ContextualAnalyticsData
			sourceName="jqlBuilderWithAi"
			sourceType={SCREEN}
			attributes={{
				isAiAvailable: isAtlassianIntelligenceEnabled,
				isAiEnabled: isNaturalLanguageMode,
			}}
		>
			<SpotlightTourContainer userPreferences={userPreferencesData}>
				<Box
					xcss={jqlBuilderWrapperStyles}
					// eslint-disable-next-line react/jsx-props-no-spreading, no-nested-ternary
					{...(!isPageHydrating && fg('render_jql_builder_in_ssr')
						? __SERVER__
							? { 'data-ssr-placeholder': 'jql-builder-with-ai' }
							: { 'data-ssr-placeholder-replace': 'jql-builder-with-ai' }
						: {})}
					data-vc={`jql-builder-with-ai${__SERVER__ && !isPageHydrating ? '-ssr' : ''}`}
				>
					{isNaturalLanguageMode ? (
						<NaturalLanguageToJQLWrapper
							{...props}
							onBack={onToggleNaturalLanguageMode}
							userPreferences={userPreferencesData}
							filters={data ?? null}
							filterRefinementQueryReference={filterRefinementQueryReference}
						/>
					) : (
						JQLBuilderWithAIComponent
					)}
				</Box>
			</SpotlightTourContainer>
		</ContextualAnalyticsData>
	);
};

export default JQLBuilderWithAI;

export const JQLBuilderWithAIEntryPoint: EntryPointComponent<{}, {}, JQLBuilderWithAIProps, {}> = ({
	props,
}) => <JQLBuilderWithAI {...props} />;

const jqlBuilderWrapperStyles = xcss({
	// JQL editor height is 52px for both "Basic" and "JQL" views
	// This can cause a layout shift when switching to the AI searchbox
	// Prior to using a min height on the common parent, the approach taken to avoid the layout shift is by using a matching margin
	// Using the min-height on the common parent is more immune for future changes in the AI search field effective height
	minHeight: '52px',
});
